import React, { useCallback, useContext, useEffect, useState } from "react";
import { useUggRouting } from "@ugitgud/legos/context/routing";
import { useBanner } from "@ugitgud/legos/hooks/useBanner";
import classNames from "classnames";
import { isFullyQualifiedURL } from "@ugitgud/legos/ui/nav/Link";
import { Season16Banner } from "./Season16Banner";
import { SEASON_16_APP_BANNER_CTA } from "./Season16Banner";

const BannerContext = React.createContext<{
  openBannerIds: string[];
  setGlobalBannerOpen: (id: string, isOpen: boolean) => void;
  hasOpenBanner: boolean;
}>({ openBannerIds: [], setGlobalBannerOpen: () => undefined, hasOpenBanner: false });

export const useGlobalBanner = () => {
  return useContext(BannerContext);
};

export const BannerProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [openBannerIds, setOpenBannerIds] = useState<Record<string, boolean>>({});
  const openBanners = Object.entries(openBannerIds)
    .filter(([_k, v]) => v)
    .map(([k, _v]) => k);
  const setGlobalBannerOpen = useCallback(
    (id: string, isOpen: boolean) => {
      setOpenBannerIds((curr) => ({ ...curr, [id]: isOpen }));
    },
    [setOpenBannerIds],
  );
  return (
    <BannerContext.Provider value={{ setGlobalBannerOpen, openBannerIds: openBanners, hasOpenBanner: openBanners.length > 0 }}>
      {children}
    </BannerContext.Provider>
  );
};

export const BannerContainer: React.FC<{
  ctaId: string;
  open?: boolean;
  onClick?: (url: string) => void;
  children: (props: { open?: boolean; onClick: (url: string) => void; onClose: () => void }) => React.ReactElement;
  className?: string;
}> = ({ children, ctaId, open: openProp, className }) => {
  const { push } = useUggRouting();
  const { setGlobalBannerOpen } = useGlobalBanner();
  const { open: hookOpen, setBannerOpen } = useBanner(ctaId);
  const open = typeof openProp === "undefined" ? hookOpen : openProp;

  useEffect(() => {
    setGlobalBannerOpen(ctaId, open);
  }, [open, setGlobalBannerOpen]);

  if (!open) {
    return null;
  }

  return (
    <div
      className={classNames(className, "overflow-y-hidden", {
        "transition-[height] !h-0": !open,
      })}
    >
      {children({
        open,
        onClick: (url) => {
          if (!isFullyQualifiedURL(url)) {
            push(url);
          } else {
            window.location.assign(url);
          }
        },
        onClose: () => setBannerOpen(false),
      })}
    </div>
  );
};

export const CurrentBanner: React.FC<{
  url?: string;
  open?: boolean;
  onClick?: (url: string) => void;
  className?: string;
}> = ({ url, ...props }) => {
  return (
    <BannerContainer ctaId={SEASON_16_APP_BANNER_CTA} {...props}>
      {(bannerProps) => <Season16Banner url={url || "https://u.gg/app"} {...bannerProps} utmSource="uggtopnav" />}
    </BannerContainer>
  );
};
